/**
 * Long-form tool guides.
 *
 * Prose lives here so the page files stay wiring + layout; ToolLayout renders
 * each guide in the quiet 'about' panel at the foot of the tool. Content is
 * unchanged from the inline blocks it replaces.
 */
import React from 'react';

export const BASE64_ABOUT = (
<>
        <p className="mb-2">
          Base64 is a binary-to-text encoding scheme that represents binary data in an ASCII string format.
          It's commonly used for:
        </p>
        <ul className="mb-0">
          <li>Encoding binary data in emails and URLs</li>
          <li>Embedding images in HTML/CSS (data URIs)</li>
          <li>Storing complex data in text-based formats like JSON or XML</li>
          <li>Authentication tokens and API keys</li>
        </ul>
      </>
);

export const URL_ABOUT = (
<>
        <p className="mb-2">
          URL encoding (also called percent encoding) converts characters into a format that can be transmitted over the Internet.
          It's essential for:
        </p>
        <ul className="mb-3">
          <li>Including special characters in URLs (spaces, symbols, etc.)</li>
          <li>Passing data as query parameters</li>
          <li>Ensuring URLs are valid and safe to use</li>
          <li>Preventing URL injection attacks</li>
        </ul>
        <p className="mb-0">
          <strong>Example:</strong> <code>Hello World!</code> becomes <code>Hello%20World%21</code>
        </p>
      </>
);

export const XML_ABOUT = (
<>
            <p className="mb-2">
              XML encoding (also called XML entity encoding) converts special characters into their entity equivalents
              to prevent them from being interpreted as XML markup. This is essential for:
            </p>
            <ul className="mb-3">
              <li>Including special characters in XML element content and attributes</li>
              <li>Preventing XML parsing errors</li>
              <li>Ensuring data integrity in XML documents</li>
              <li>Protecting against XML injection attacks</li>
            </ul>
            <h3 className="h6">Common XML Entities:</h3>
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Character</th>
                  <th>Entity</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>&amp;</code></td>
                  <td><code>&amp;amp;</code></td>
                  <td>Ampersand</td>
                </tr>
                <tr>
                  <td><code>&lt;</code></td>
                  <td><code>&amp;lt;</code></td>
                  <td>Less than</td>
                </tr>
                <tr>
                  <td><code>&gt;</code></td>
                  <td><code>&amp;gt;</code></td>
                  <td>Greater than</td>
                </tr>
                <tr>
                  <td><code>"</code></td>
                  <td><code>&amp;quot;</code></td>
                  <td>Double quote</td>
                </tr>
                <tr>
                  <td><code>'</code></td>
                  <td><code>&amp;apos;</code></td>
                  <td>Single quote (apostrophe)</td>
                </tr>
              </tbody>
            </table>
            <p className="mb-0">
              <strong>Example:</strong> <code>&lt;tag&gt;Hello &amp; Goodbye&lt;/tag&gt;</code> becomes{' '}
              <code>&amp;lt;tag&amp;gt;Hello &amp;amp; Goodbye&amp;lt;/tag&amp;gt;</code>
            </p>
          </>
);

export const JSON_YAML_ABOUT = (
<>
            <div className="tool-columns--two">
              <div>
                <h3 className="h6">JSON (JavaScript Object Notation)</h3>
                <ul className="mb-3">
                  <li>Lightweight data-interchange format</li>
                  <li>Easy for humans to read and write</li>
                  <li>Easy for machines to parse and generate</li>
                  <li>Language independent</li>
                  <li>Uses curly braces and square brackets</li>
                </ul>
              </div>
              <div>
                <h3 className="h6">YAML (YAML Ain't Markup Language)</h3>
                <ul className="mb-3">
                  <li>Human-friendly data serialization format</li>
                  <li>More readable than JSON for complex structures</li>
                  <li>Uses indentation for structure</li>
                  <li>Supports comments</li>
                  <li>Common in configuration files</li>
                </ul>
              </div>
            </div>
            <h3 className="h6">Common Use Cases:</h3>
            <ul className="mb-0">
              <li><strong>JSON:</strong> APIs, web services, data storage, configuration</li>
              <li><strong>YAML:</strong> Docker Compose, Kubernetes configs, CI/CD pipelines, Ansible playbooks</li>
            </ul>
          </>
);

export const HASH_ABOUT = (
<>
            <p className="mb-3">
              Hash functions are cryptographic algorithms that convert input data of any size into a fixed-size string
              of characters. The output, called a hash or digest, is unique to the input data.
            </p>

            <h3 className="h6">Common Hash Algorithms:</h3>
            <div className="tool-columns--two">
              <div>
                <ul>
                  <li>
                    <strong>MD5 (128-bit):</strong> Fast but outdated. Not recommended for security
                    purposes. Still used for checksums and non-cryptographic applications.
                  </li>
                  <li>
                    <strong>SHA-1 (160-bit):</strong> Deprecated for security. Vulnerable to collision attacks.
                    Legacy support only.
                  </li>
                  <li>
                    <strong>SHA-256 (256-bit):</strong> Part of SHA-2 family. Widely used and secure.
                    Used in Bitcoin and SSL certificates.
                  </li>
                </ul>
              </div>
              <div>
                <ul>
                  <li>
                    <strong>SHA-512 (512-bit):</strong> Stronger variant of SHA-2. More secure but
                    slower than SHA-256.
                  </li>
                  <li>
                    <strong>SHA-3:</strong> Latest member of the Secure Hash Algorithm family.
                    Different internal structure from SHA-2.
                  </li>
                  <li>
                    <strong>RIPEMD-160 (160-bit):</strong> Used in Bitcoin addresses alongside SHA-256.
                  </li>
                </ul>
              </div>
            </div>

            <h3 className="h6 mt-3">Common Use Cases:</h3>
            <ul className="mb-0">
              <li>Password storage (with salt)</li>
              <li>File integrity verification</li>
              <li>Digital signatures</li>
              <li>Blockchain and cryptocurrencies</li>
              <li>Data deduplication</li>
              <li>Cache keys and unique identifiers</li>
            </ul>

            <div className="alert alert-warning mt-3 mb-0">
              <strong>Security Note:</strong> Never use simple hashing for password storage in production.
              Always use purpose-built password hashing functions like bcrypt, Argon2, or PBKDF2 with
              proper salting.
            </div>
          </>
);

export const JWT_ABOUT = (
  <>
    <p className="mb-3">
      A JSON Web Token packs three base64url segments into one string:{' '}
      <code>header.payload.signature</code>. The header names the signing algorithm, the payload
      carries the claims, and the signature proves the first two were not altered.
    </p>

    <h3 className="h6">Claims worth knowing:</h3>
    <div className="tool-columns--two">
      <div>
        <ul>
          <li><strong>iss</strong> — who issued the token</li>
          <li><strong>sub</strong> — who or what it is about</li>
          <li><strong>aud</strong> — who is meant to accept it</li>
        </ul>
      </div>
      <div>
        <ul>
          <li><strong>exp</strong> — rejected from this instant on</li>
          <li><strong>nbf</strong> — rejected until this instant</li>
          <li><strong>iat</strong> — when it was minted</li>
        </ul>
      </div>
    </div>

    <div className="alert alert-warning mt-3 mb-0">
      <strong>Decoding is not verification.</strong> This tool reads the header and payload but never
      checks the signature — that needs the issuer's key, which should never be pasted into a web
      page. A decoded token is untrusted input until your backend verifies it.
    </div>
  </>
);

export const UUID_ABOUT = (
  <>
    <p className="mb-3">
      A UUID is a 128-bit identifier you can mint anywhere without coordinating with a central
      service. Values here come from <code>crypto.getRandomValues()</code>, the platform's
      cryptographically secure random source.
    </p>

    <h3 className="h6">Choosing a version:</h3>
    <ul className="mb-3">
      <li>
        <strong>Version 4 (random):</strong> 122 random bits. Reveals nothing about when or where it
        was created, and sorts in no meaningful order.
      </li>
      <li>
        <strong>Version 7 (time-ordered):</strong> a 48-bit millisecond timestamp followed by random
        bits, so values sort by creation time and B-tree inserts stay at the end of the index.
      </li>
    </ul>

    <p className="mb-0">
      <strong>Collision odds:</strong> you would need to generate about 2.7 &times; 10<sup>18</sup>{' '}
      v4 UUIDs before a duplicate becomes as likely as not — roughly a billion per second for 85
      years.
    </p>
  </>
);

export const TIMESTAMP_ABOUT = (
  <>
    <p className="mb-3">
      Unix time is the number of seconds since 1 January 1970 UTC, ignoring leap seconds. It is
      compact and unambiguous, which is why logs, tokens and APIs all speak it — and unreadable,
      which is why this page exists.
    </p>

    <h3 className="h6">Telling the units apart:</h3>
    <ul className="mb-3">
      <li><strong>10 digits</strong> — seconds, e.g. <code>1516239022</code></li>
      <li><strong>13 digits</strong> — milliseconds, e.g. <code>1516239022000</code></li>
      <li><strong>16 digits</strong> — microseconds, e.g. <code>1516239022000000</code></li>
    </ul>

    <p className="mb-0">
      JWT claims (<code>exp</code>, <code>iat</code>, <code>nbf</code>) are always seconds.
      JavaScript's <code>Date.now()</code> is milliseconds. Mixing the two by a factor of 1000 is the
      single most common bug in this area.
    </p>
  </>
);

export const REGEX_ABOUT = (
  <>
    <p className="mb-3">
      This tester runs your pattern through the browser's own <code>RegExp</code> engine, so its
      behaviour matches what <code>match</code>, <code>replace</code> and <code>split</code> will do
      in your JavaScript or Node code.
    </p>

    <h3 className="h6">Handy building blocks:</h3>
    <div className="tool-columns--two">
      <div>
        <ul>
          <li><code>\d \w \s</code> — digit, word character, whitespace</li>
          <li><code>+ * ?</code> — one or more, any number, optional</li>
          <li><code>{'{2,5}'}</code> — between two and five</li>
        </ul>
      </div>
      <div>
        <ul>
          <li><code>(...)</code> — capture group, referenced as <code>$1</code></li>
          <li><code>(?&lt;name&gt;...)</code> — named group, <code>$&lt;name&gt;</code></li>
          <li><code>(?:...)</code> — group without capturing</li>
        </ul>
      </div>
    </div>

    <div className="alert alert-warning mt-3 mb-0">
      <strong>Watch the backtracking.</strong> Nested quantifiers such as <code>(a+)+$</code> can
      take exponential time on a non-matching string. The tester caps how many matches it collects,
      but the same pattern would stall a real server.
    </div>
  </>
);
